import React from "react";
import { useLocation, useNavigate } from "react-router-dom";

interface SidebarButtonProps {
  icon: React.ReactNode;
  label: string;
  path: string;
  isMinimized: boolean;
  primaryColor: string;
  primaryColorHover: string;
  getBasePath: () => string;
  onNavigate?: () => void;
  /** Indented sub-item under an expandable group */
  nested?: boolean;
}

export const SidebarButton: React.FC<SidebarButtonProps> = ({
  icon,
  label,
  path,
  isMinimized,
  primaryColor,
  primaryColorHover: _primaryColorHover,
  getBasePath,
  onNavigate,
  nested = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();

  const fullPath = `${getBasePath()}${path}`;
  const isActive =
    location.pathname === fullPath ||
    location.pathname.startsWith(`${fullPath}/`);

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    navigate(`${getBasePath()}${path}`);
    e.currentTarget.blur();
    if (onNavigate) {
      onNavigate();
    }
  };

  return (
    <button
      type="button"
      className={`${
        isMinimized
          ? "w-12 h-12 mx-auto flex items-center justify-center rounded-full"
          : `w-full flex items-center gap-2 sm:gap-3 py-2 sm:py-3 rounded-xl ${
              nested ? "pl-2 pr-3 sm:pr-4 text-sm" : "px-3 sm:px-4"
            }`
      } font-medium transition-all duration-200 text-sm sm:text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 ${
        isActive
          ? "bg-white text-gray-900 shadow-md dark:bg-slate-700 dark:text-white"
          : "text-gray-700 dark:text-slate-300 hover:bg-white dark:hover:bg-slate-800 hover:text-[color:var(--sidebar-hover)]"
      }`}
      style={
        isActive
          ? { boxShadow: `inset 3px 0 0 ${primaryColor}` }
          : ({ ["--sidebar-hover" as string]: primaryColor } as React.CSSProperties)
      }
      onClick={handleClick}
      title={isMinimized ? label : undefined}
    >
      <span className="flex items-center justify-center [&>.MuiSvgIcon-root]:!text-[1.25rem] sm:[&>.MuiSvgIcon-root]:!text-[1.125rem]">
        {icon}
      </span>
      {!isMinimized && <span className="truncate">{label}</span>}
    </button>
  );
};
